import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { LinkedAppointmentSelect } from "@/Common/Components/LinkedAppointmentSelect/LinkedAppointmentSelect";
import { Popup } from "@/Common/Components/Modals/Popup";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { Guid } from "@/Common/Models/Guid";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { MedicalNoteHelper } from "@/Patient/Helpers/MedicalNoteHelper";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";

import styles from "./MedicalNoteEditConsultationDetailsPopup.module.scss";

import LinkedAppointment from "@/Globals/_images/linked-appointment-icon.svg";
import UnLinkedAppointment from "@/Globals/_images/unlinked-appointment-icon.svg";

interface IMedicalNoteEditConsultationDetailsPopupsProps {
    noteModel: IPatientMedicalNoteModel;
    patientContactId?: string;
}

class MedicalNoteEditConsultationDetailsPopupState {
    public availableConsultationTypes: FlatLookupView[];
    public selectedSiteId: Guid;
    public selectedConsultationTypeLookupId: string;
    public selectedConsultationContextLookupId: string;
    public consultationDate: Date;
    public selectedDepartmentId: Guid;
    public costCenters: PrivateBillCostCenter[];
    public isActiveSite: boolean;
    public isActiveSiteDepartment: boolean;
    public isCheckBoxShown: boolean;
    public selectedLinkAppointmentId?: string;
    public isLinkedAppointmentChanged: boolean;
}

class MedicalNoteEditConsultationDetailsPopup extends BaseReactComponent<IMedicalNoteEditConsultationDetailsPopupsProps, MedicalNoteEditConsultationDetailsPopupState> {
    private id: string = "noteconsultationdateeditdetails";

    private get encounter(): Encounter {
        return this.props.noteModel.get("Encounter");
    }

    private get appointmentList(): PatientAppointmentListItem[] {
        return patientStore.PatientHistoryAppointmentsModels[this.props.patientContactId].get("AppointmentList").toArray();
    }

    private get isModificationLoading(): boolean {
        return patientStore.PatientMedicalNoteModels[this.props.patientContactId].get("IsModificationLoading");
    }

    private get consultationContextList(): FlatLookupView[] {
        return cacheStore.FindLookupsByTypeCode("CONSULTATIONCONTEXT");
    }

    constructor(props: IMedicalNoteEditConsultationDetailsPopupsProps) {
        super(props);

        const initialState = new MedicalNoteEditConsultationDetailsPopupState();

        const employeeUIModel = sessionStore.getCurrentEmployee();
        const consultationTypes = employeeUIModel ? employeeUIModel.getServiceSpecializations() : [];

        if (!IsNullOrWhiteSpace(this.encounter.ConsultationTypeLookupId) && (!consultationTypes?.some(p => p.LookupID == this.encounter.ConsultationTypeLookupId)))
            consultationTypes.push(cacheStore.GetLookupById(this.encounter.ConsultationTypeLookupId));

        initialState.selectedSiteId = this.encounter.SiteId;
        initialState.selectedConsultationTypeLookupId = this.encounter.ConsultationTypeLookupId;
        initialState.selectedConsultationContextLookupId = this.encounter.ConsultationContextLookupId;
        initialState.consultationDate = new Date(this.encounter.ConsultationDate.toString());
        initialState.availableConsultationTypes = consultationTypes;

        initialState.costCenters = this.encounter.SiteId == null ? null : cacheStore.getCostCenterBySiteId(this.encounter.SiteId);
        initialState.selectedDepartmentId = this.encounter.CostCenterId;
        initialState.selectedLinkAppointmentId = this.encounter.AppointmentId ?? this.encounter.SuggestedAppointmentLinkId;
        initialState.isLinkedAppointmentChanged = false;
        if (employeeUIModel?.Model.AssociatedSites.some(p => p === this.encounter.SiteId)) {
            initialState.isActiveSiteDepartment =
                this.encounter.SiteId != null &&
                initialState.selectedDepartmentId != null;
            initialState.isCheckBoxShown = true;
            initialState.isActiveSite = true;
        }

        this.state = initialState;
    }

    public componentDidMount(): void {
        patientStore.ActionsHistory.LoadPatientAppointment(this.props.patientContactId, false, null, null, null, "");
    }

    public render(): JSX.Element {
        return (
            <Popup
                Id={this.id}
                title={cacheStore.GetString("ConsultationDetailsEditLabel")}
                closeTarget={LocalPatientOperations.MedicalNote.ConsultationDetailsPopupClose}
                draggable
                centeredHorizontally
                isOverflowVisible
                width={550}
                headerClass={styles.popupHeader}
            >
                <div className="p-body" data-cy="medicalNote-editConsultation-popup">
                    <div className={`row ${styles.fieldContainer}`}>
                        <div className="col-sm-12">
                            <label className="form-label">{cacheStore.GetString("ConsultationTypeDLabel")}</label>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <select
                                data-cy="medicalNote-editConsultation-type"
                                onChange={(e: any) => this.handleConsultationTypeChanged(e.target.value)}
                                className="form-control myle-control"
                                value={this.state.selectedConsultationTypeLookupId}
                            >
                                {this.state.availableConsultationTypes.map(type =>
                                    <option key={type.LookupID} value={type.LookupID}>{type.GetValue()}</option>
                                )}
                            </select>
                        </div>
                    </div>
                    <div className={`row ${styles.fieldContainer}`}>
                        <div className="col-sm-12">
                            <label className="form-label">{cacheStore.GetString("ContextDLabel")}</label>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <select
                                data-cy="medicalNote-editConsultation-context"
                                onChange={(e: any) => this.handleContextTypeChanged(e.target.value)}
                                className="form-control myle-control"
                                value={this.state.selectedConsultationContextLookupId}
                            >
                                {this.consultationContextList.map(type =>
                                    <option key={type.LookupID} value={type.LookupID}>{type.GetValue()}</option>
                                )}
                            </select>
                        </div>
                    </div>

                    {cacheStore.IsMultiSite() == true && (
                        <div className={styles.fieldContainer}>
                            <div className="row">
                                <div className="col-sm-12">
                                    <label className="form-label">{cacheStore.GetString("SiteDLabel")}</label>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <ComboBoxControl
                                        dataCy="medicalNote-editConsultation-site"
                                        selectedCode={this.state.selectedSiteId}
                                        items={Encounter.getCurrentUserEnabledSitesAsComboBoxItems(this.encounter)}
                                        hasEmptyItem={false}
                                        id="_SiteCombobox"
                                        onChange={selectedCode => this.onSiteChanged(GuidHelper.AsGuid(selectedCode))}
                                        maxNumberOptionCharacters={70}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {(this.state.costCenters && this.state.costCenters.length > 0) && (
                        <div className={styles.fieldContainer}>
                            <div className="row">
                                <div className="col-sm-12">
                                    <label className="form-label">{cacheStore.GetString("DepartmentLabel")}</label>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-12">
                                    <Combobox
                                        selectedCode={this.state.selectedDepartmentId}
                                        items={Immutable.List(this.state.costCenters.map(x => new ComboBoxDropDownItem(x.ID, PrivateBillCostCenter.GetDisplayName(x), 0)))}
                                        HasEmptyItem={true}
                                        id="_departmentCombobox"
                                        onChange={selectedCode => this.mutateState(s => s.selectedDepartmentId = GuidHelper.AsGuid(selectedCode))}
                                        maxNumberOptionCharacters={60}
                                        doInitialChangeOp={false}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {this.state.isCheckBoxShown &&
                        this.renderCheckBoxActiveSiteDepartment()
                    }

                    <div className={`row ${styles.fieldContainer}`}>
                        <div className="col-sm-12">
                            <label className="form-label">{cacheStore.GetString("ConsultationDateDLabel")}</label>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div>
                                    <DateSelector
                                        dateFormat="yyyy-MM-dd"
                                        id="consultation_date"
                                        dataCy="medicalNote-editConsultation-date"
                                        cssClass="rounded"
                                        date={this.state.consultationDate}
                                        onChangeCallback={date => this.handleDateChanged(date)}
                                        disabled={this.props.noteModel.get("Encounter").IsSignedOff == true}
                                    />
                                </div>
                                <div style={{ marginLeft: "5px" }}>
                                    <TimeSelector
                                        id="time_001_encounterTime"
                                        dataCy="medicalNote-editConsultation-time"
                                        onChangeCallback={date =>
                                            this.mutateState(s => {
                                                s.consultationDate = s.consultationDate ?? DateTime.nowDate();
                                                s.consultationDate.setHours(date.getHours());
                                                s.consultationDate.setMinutes(date.getMinutes());
                                            })
                                        }
                                        date={this.state.consultationDate}
                                        disabled={this.props.noteModel.get("Encounter").IsSignedOff == true}
                                        isRequired
                                    />
                                </div>
                            </div>
                            <span>
                                {this.props.noteModel.get("Encounter").IsSignedOff != true && (
                                    <a
                                        data-cy="medicalNote-editConsultation-today"
                                        onClick={() => this.handleDateChanged(DateTime.nowDeprecated())}
                                    >
                                        {cacheStore.GetString("TodayLabel")}
                                    </a>
                                )}
                            </span>
                        </div>
                    </div>
                    {cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote && (
                        <div className={styles.fieldContainer}>
                            <div className="row">
                                <div className="col-sm-12">
                                    <label className={`form-label ${styles.linkAppointmentLabel}`}>{cacheStore.GetString("LinkedAppointmentLabel")}</label>
                                    {this.state.selectedLinkAppointmentId ?
                                        <LinkedAppointment /> : <UnLinkedAppointment />
                                    }
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <LinkedAppointmentSelect
                                        appointmentList={this.appointmentList}
                                        onChangeCallback={value => {
                                            this.mutateState(s => {
                                                s.selectedLinkAppointmentId = value;
                                                s.isLinkedAppointmentChanged = true;
                                            });
                                        }}
                                        value={this.state.selectedLinkAppointmentId}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                            <div>
                                <div>
                                    <button
                                        data-cy="medicalNote-editConsultation-cancel"
                                        data-action="cancel"
                                        className="btn btn-default push-right-5"
                                        onClick={() => this.handleCancelClicked()}
                                    >{cacheStore.GetString("CancelLabel")}
                                    </button>
                                    <button
                                        data-cy="medicalNote-editConsultation-save"
                                        className="btn btn-primary"
                                        disabled={!this.state.consultationDate || this.isModificationLoading}
                                        onClick={() => this.handleModifyClicked()}
                                    >{cacheStore.GetString("ModifyLabel")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private handleConsultationTypeChanged(id: string): void {
        this.mutateState(s => s.selectedConsultationTypeLookupId = id);
    }

    private handleContextTypeChanged(id: string): void {
        this.mutateState(s => s.selectedConsultationContextLookupId = id);
    }

    private handleDateChanged(date: Date): void {
        let autoLinkAppointmentId = null;
        if (this.encounter.EnableAppointmentLinkSuggestions) {
            autoLinkAppointmentId = MedicalNoteHelper.AutoLinkAppointmentToEncounter(this.encounter, this.appointmentList, date);

            if (DateHelper.formatDate(this.encounter.ConsultationDate, "YYYY-MM-DD") !== DateHelper.formatDate(date, "YYYY-MM-DD")
                && this.encounter.AppointmentId
                && this.encounter.AppointmentId !== autoLinkAppointmentId) {
                this.mutateState(s => { s.isLinkedAppointmentChanged = true; });
            }
        }

        this.mutateState(s => {
            if (date == null) {
                s.consultationDate = null;
                return;
            }

            let hour = 0;
            let min = 0;
            if (s.consultationDate) {
                hour = s.consultationDate.getHours();
                min = s.consultationDate.getMinutes();
            }
            s.consultationDate = date;
            s.consultationDate.setHours(hour);
            s.consultationDate.setMinutes(min);
            s.selectedLinkAppointmentId = autoLinkAppointmentId;
        });
    }

    private handleCancelClicked(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupClose);
    }

    private handleModifyClicked(): void {
        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.ConsultationDetailsPopupModify,
            {
                consultationDate: this.state.consultationDate,
                consultationTypeId: this.state.selectedConsultationTypeLookupId,
                consultationContextId: this.state.selectedConsultationContextLookupId,
                consultationSiteId: this.state.selectedSiteId,
                consultationDepartmentId: this.state.selectedDepartmentId,
                consultationIsActiveSite: this.state.isActiveSite,
                consultationIsActiveDepartment: this.state.isActiveSiteDepartment && this.state.selectedDepartmentId != null,
                appointmentId: this.state.selectedLinkAppointmentId,
                enableAppointmentLinkSuggestions: this.state.isLinkedAppointmentChanged ? false : this.encounter.EnableAppointmentLinkSuggestions
            }
        );
    }

    private onSiteChanged(siteId: Guid): void {
        const employeeUIModel = sessionStore.getCurrentEmployee();
        const currentUser = employeeUIModel && employeeUIModel.Model;

        this.mutateState(s => {
            s.selectedSiteId = siteId;
            s.costCenters = cacheStore.getCostCenterBySiteId(s.selectedSiteId);
            if (!currentUser.AssociatedSites.some(x => x === siteId)) {
                s.isCheckBoxShown = false;
                s.isActiveSite = false;
                s.isActiveSiteDepartment = false;
            }
            else {
                s.isCheckBoxShown = true;
                s.isActiveSite = true;
                s.isActiveSiteDepartment = true;
            }
            if (s.costCenters && s.costCenters.length > 0)
                s.selectedDepartmentId = s.costCenters[0].ID;
            else {
                s.selectedDepartmentId = null;
                s.isActiveSiteDepartment = false;
            }
        });
    }

    private setActiveSite(isChecked: boolean): void {
        this.mutateState(s => {
            s.isActiveSite = isChecked;
            s.isActiveSiteDepartment = false;
        });
    }

    private setActiveSiteDepartment(isChecked: boolean): void {
        this.mutateState(s => {
            s.isActiveSite = isChecked;
            s.isActiveSiteDepartment = isChecked;
        });
    }

    private renderCheckBoxActiveSiteDepartment(): JSX.Element {
        const isActiveSiteDepartment = this.state.costCenters && this.state.costCenters.length > 0;

        return (
            <div className="display-flex-justified-end margintop20">
                <div>
                    <input
                        data-cy="medicalNote-editConsultation-isActiveSite"
                        type="checkbox"
                        onChange={e => isActiveSiteDepartment ? this.setActiveSiteDepartment(e.target.checked) : this.setActiveSite(e.target.checked)}
                        checked={isActiveSiteDepartment ? this.state.isActiveSiteDepartment : this.state.isActiveSite}
                    />
                </div>
                <div className="marginleft5">
                    <label onClick={() => isActiveSiteDepartment ? this.setActiveSiteDepartment(!this.state.isActiveSite) : this.setActiveSite(!this.state.isActiveSite)}>
                        {isActiveSiteDepartment ? cacheStore.GetString("SetActiveSiteDepartmentLabel") : cacheStore.GetString("SetActiveSiteLabel")}
                    </label>
                </div>
            </div>
        );
    }
}

export { MedicalNoteEditConsultationDetailsPopup };
